import React, { useEffect, useState } from "react";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { Product, SelectedProduct } from "~/common/Store/models/ProductListing";
import { User } from "~/common/Store/models/User";
import Standard from "./Price";
import MapPrice from "./Map";
import UnitOfMeasure from "../UnitOfMeasure";
import ProductReplacement from "../ProductReplacement";
import CrossedOutPrice from "./CrossedOutPrice";
import PlusMemberDiscount from "./PlusMemberDiscount";
import { defaultChatObject } from "~/common/Store/rootReducer";
import applyBelowMapPricing from "./applyBelowMapPricing";
import applyPlusPricing from "../applyPlusPricing";
import { getShowFromPricing } from "./pricingHelper";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import itemHasMapStyle from "../itemHasMapStyle";
import getLowestPrice from "../getLowestPrice";
import isActiveSale from "../isActiveSale";
import QuantityDiscounts from "./QuantityDiscounts";
import DivideByPricing from "./DivideByPricing";

interface Props {
  product: Product;
  mounted: boolean;
  user: User;
  selectedProduct: SelectedProduct;
  hasCustomizableProductsOverride?: boolean;
  userHasSelectedGroupMember?: boolean;
  isOutlet?: boolean;
  productListingView?: string;
  preselectedVgMemberItemNumber?: string;
  hideQuantityDiscountTable?: boolean;
  customizablePricingExtraClassOne?: string;
  customizablePricingExtraClassTwo?: string;
  setIsQuantityDiscountPopoverOpen?: React.Dispatch<
    React.SetStateAction<string>
  >;
  isBrandAd?: boolean;
}

const Pricing = ({
  product,
  mounted,
  user,
  selectedProduct,
  hasCustomizableProductsOverride = false,
  userHasSelectedGroupMember,
  productListingView,
  preselectedVgMemberItemNumber = "",
  hideQuantityDiscountTable = false,
  customizablePricingExtraClassOne = "",
  customizablePricingExtraClassTwo = "",
  setIsQuantityDiscountPopoverOpen,
  isBrandAd,
}: Props) => {
  const { itemNumber, minimumCustomizationPrice } = product;
  const showFrom =
    preselectedVgMemberItemNumber.length > 0
      ? false
      : getShowFromPricing(
          product,
          selectedProduct,
          userHasSelectedGroupMember
        );

  const {
    unitOfMeasure,
    price,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    activeSale,
    isOutlet,
  } = selectedProduct;

  const [
    displayOutletCallForPricing,
    setDisplayOutletCallForPricing,
  ] = useState(false);

  useEffect(() => {
    if (product.outletAttributes?.callForPricing) {
      setDisplayOutletCallForPricing(
        product.outletAttributes.callForPricing && isOutlet
      );
    }
  }, [product.outletAttributes, isOutlet, mounted]);

  const hasMapStyle = itemHasMapStyle(price, activeSale);

  const displayMapStyle =
    (hasMapStyle || displayOutletCallForPricing) &&
    (price.displayMinimumAdvertisedPrice ||
      price.applyTypeLPriceToPlatinumPlus);

  const applyBelowMapPrice = applyBelowMapPricing(
    {
      price,
      showBelowMinimumAdvertisedPriceForLoggedInCustomers,
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    },
    user
  );

  const isSalePriceTheLowest = (() => {
    if (selectedProduct.price.salePrice <= 0) {
      return false;
    }

    const lowestPrice = getLowestPrice(
      selectedProduct.price,
      user,
      selectedProduct.activeSale,
      selectedProduct.showBelowMinimumAdvertisedPriceForLoggedInCustomers,
      selectedProduct.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers
    );

    return lowestPrice === selectedProduct.price.salePrice;
  })();

  const showSalePrice =
    isActiveSale(selectedProduct.price) && isSalePriceTheLowest;

  const hasMapStyleWithOverridesOn =
    hasMapStyle &&
    user.registered &&
    (showBelowMinimumAdvertisedPriceForLoggedInCustomers ||
      (showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers &&
        (user.hasWebPlus || user.platinum.enabled)));

  const isMustBuyHigherThanQuantityDiscount = () => {
    if (!product.price.hasQuantityDiscounts || !product.mustBuy) {
      return -1;
    }

    const largestQuantityDiscount = product.price.quantityDiscounts.at(-1);

    const lowestQuantityDiscountPrice = largestQuantityDiscount?.price;

    if (
      lowestQuantityDiscountPrice &&
      largestQuantityDiscount.quantity <= product.mustBuy &&
      lowestQuantityDiscountPrice < product.price.price
    ) {
      return lowestQuantityDiscountPrice;
    }

    return -1;
  };

  const quantityDiscountPrice = isMustBuyHigherThanQuantityDiscount();

  const displayQuantityDiscount =
    price.hasQuantityDiscounts &&
    quantityDiscountPrice < 0 &&
    (!hasMapStyle || hasMapStyleWithOverridesOn || applyBelowMapPrice);

  if (
    !displayQuantityDiscount &&
    applyPlusPricing(price, activeSale, hasMapStyle, !!applyBelowMapPrice)
  ) {
    return (
      <PlusMemberDiscount
        productListingView={productListingView}
        selectedProduct={selectedProduct}
        user={user}
        isBrandAd={isBrandAd}
      />
    );
  }

  const isProductDiscontinued =
    product.productReplacement &&
    product.productReplacement.replacementItemNumber.length > 0;

  const shouldDisplayCustomizable = () => {
    if (displayMapStyle && product.customizationRequired && !user.registered) {
      return false;
    }
    return (
      !isOutlet &&
      ((hasCustomizableProductsOverride && product.isCustomizable) ||
        shouldDisplayRequiredCustomization(product))
    );
  };

  if (shouldDisplayCustomizable()) {
    return (
      <p className={`price flex flex-col ${customizablePricingExtraClassOne}`}>
        <span
          className={`each custom-price ${customizablePricingExtraClassTwo}`}
        >
          Customized Prices Starting at
        </span>
        <span>
          {`${formatAsCurrency.formatIfUndefined(
            minimumCustomizationPrice || selectedProduct.price.price
          )}`}
          <UnitOfMeasure
            unitOfMeasure={unitOfMeasure}
            showBoldOutletFormatting={isOutlet}
          />
        </span>
        <DivideByPricing
          dataTestId="divideByPricing"
          price={
            selectedProduct.minimumCustomizationPrice ??
            selectedProduct.price.price
          }
          divideByUnit={selectedProduct.divideByUnit}
          divideQuantity={selectedProduct.divideQuantity}
        />
      </p>
    );
  }

  if (!isOutlet && isProductDiscontinued) {
    return <ProductReplacement productListingView={productListingView} />;
  }

  if (displayMapStyle) {
    return (
      <MapPrice
        {...{
          itemNumber,
          price,
          unitOfMeasure,
          mounted,
          user,
          showBelowMinimumAdvertisedPriceForLoggedInCustomers,
          showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
          product,
          selectedProduct,
          chat: defaultChatObject,
          productListingView,
          setIsQuantityDiscountPopoverOpen,
          displayOutletCallForPricing,
        }}
      />
    );
  }

  if (price.hasQuantityDiscounts && displayQuantityDiscount && !isOutlet) {
    return (
      <QuantityDiscounts
        productListingView={productListingView}
        selectedProduct={selectedProduct}
        user={user}
        hideQuantityDiscountTable={hideQuantityDiscountTable}
        displaySalePrice={showSalePrice}
        setIsQuantityDiscountPopoverOpen={setIsQuantityDiscountPopoverOpen}
      />
    );
  }

  return (
    <>
      {isOutlet && (
        <CrossedOutPrice
          price={selectedProduct.price.price}
          originalPrice={selectedProduct.price.originalPrice}
          showOriginalOutletPrice={isOutlet}
        />
      )}
      <Standard
        showFrom={showFrom}
        price={selectedProduct.price.price}
        unitOfMeasure={unitOfMeasure}
        productListingView={productListingView}
        isOutlet={isOutlet}
        displaySalePrice={showSalePrice}
        salePrice={showSalePrice ? selectedProduct.price.salePrice : 0}
        quantityDiscountPrice={quantityDiscountPrice}
      />
      <DivideByPricing
        dataTestId="divideByPricing"
        price={
          showSalePrice
            ? selectedProduct.price.salePrice
            : selectedProduct.price.price
        }
        divideByUnit={selectedProduct.divideByUnit}
        divideQuantity={selectedProduct.divideQuantity}
        quantityDiscountPrice={quantityDiscountPrice}
      />
      {isOutlet && (
        <>
          <p className="leading-snug text-red-700 text-2xs mb-1 mt-1">
            Outlet Savings:{" "}
            <b>{`${formatAsCurrency.formatIfUndefined(
              (selectedProduct.price.originalPrice ??
                selectedProduct.price.price) - selectedProduct.price.price
            )}`}</b>
          </p>
        </>
      )}
    </>
  );
};

export default Pricing;
